<script setup lang="ts">
/**
 * IFC engine (ThatOpen components + fragments).
 *
 * Implements ViewerApi (src/viewer/types.ts). The UI lives in ViewerWorkspace,
 * which drives this component through a template ref. In `minimal` mode
 * (landing hero, How it works) the viewer is a decorative auto-rotating render
 * with no picking or tools.
 *
 * Ids exposed through the API are IFC GlobalIds. Tree nodes that group the
 * elements of a storey by class have synthetic ids 'group:<parent>:<IFCCLASS>'.
 */
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick, type ShallowUnwrapRef } from 'vue'
import * as OBC from '@thatopen/components'
import * as THREE from 'three'
import CameraControls from 'camera-controls'
import { RenderedFaces, SnappingClass, type FragmentsModel, type MaterialDefinition, type RaycastResult, type SpatialTreeItem } from '@thatopen/fragments'
import fragmentsWorkerUrl from '@thatopen/fragments/worker?url'
import { canvasToOgBlob } from '@/composables/useThumbnails'
import { useTheme, type ResolvedTheme } from '@/composables/useTheme'
import { useI18n } from '@/composables/useI18n'
import {
  newId,
  type CameraPreset,
  type CameraState,
  type ClipAxis,
  type ClipPlaneState,
  type ColorOverride,
  type EngineState,
  type GroupInfo,
  type MeasurementInfo,
  type PickResult,
  type Projection,
  type ScreenPoint,
  type SelectionInfo,
  type TreeNode,
  type Vec3,
  type ViewerApi,
  type ViewerFeature,
  type ViewerTool,
  type ViewState,
} from '@/viewer/types'
import { prettyIfcClass } from '@/viewer/ifc/names'
import { itemName, itemToGroups } from '@/viewer/ifc/properties'
import { MeasureOverlay, type SnapKind } from '@/viewer/ifc/overlay'

// Scene colors per theme (keep viewerBg in sync with --viewer-bg in theme.css)
const SCENE_PALETTE: Record<ResolvedTheme, {
  viewerBg: number
  grid: number
  blueprint: number
  blueprintOpacity: number
  shadowOpacity: number
  sky: number
  ground: number
}> = {
  dark: {
    viewerBg: 0x08080a,
    grid: 0x3a3a44,
    blueprint: 0x5b8cff,
    blueprintOpacity: 0.55,
    shadowOpacity: 0.6,
    sky: 0xbcd4ff,
    ground: 0x1a1410,
  },
  light: {
    viewerBg: 0xdfe1e6,
    grid: 0x9aa0ad,
    blueprint: 0x2b4a8f,
    blueprintOpacity: 0.32,
    shadowOpacity: 0.28,
    sky: 0xffffff,
    ground: 0xc9c2b8,
  },
}

/**
 * Infinite-looking technical floor: minor/major lines that fade out radially.
 * Uses world-space XZ so lines stay put while the camera orbits.
 */
function createBlueprintFloor(center: THREE.Vector3, radius: number, y: number) {
  const major = Math.pow(10, Math.floor(Math.log10(Math.max(radius, 1) / 1.5)))
  const material = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: {
      uColor: { value: new THREE.Color() },
      uOpacity: { value: 0.5 },
      uMinor: { value: major / 5 },
      uMajor: { value: major },
      uCenter: { value: new THREE.Vector2(center.x, center.z) },
      uFade: { value: radius * 3.2 },
    },
    vertexShader: /* glsl */ `
      varying vec3 vWorld;
      void main() {
        vec4 world = modelMatrix * vec4(position, 1.0);
        vWorld = world.xyz;
        gl_Position = projectionMatrix * viewMatrix * world;
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 uColor;
      uniform float uOpacity;
      uniform float uMinor;
      uniform float uMajor;
      uniform vec2 uCenter;
      uniform float uFade;
      varying vec3 vWorld;

      float gridLine(vec2 p, float size) {
        vec2 coord = p / size;
        vec2 g = abs(fract(coord - 0.5) - 0.5) / fwidth(coord);
        return 1.0 - min(min(g.x, g.y), 1.0);
      }

      void main() {
        vec2 p = vWorld.xz;
        float minor = gridLine(p, uMinor) * 0.35;
        float major = gridLine(p, uMajor);
        float d = length(p - uCenter);
        float fade = 1.0 - smoothstep(uFade * 0.15, uFade, d);
        float a = max(minor, major) * fade * uOpacity;
        if (a < 0.003) discard;
        gl_FragColor = vec4(uColor, a);
      }
    `,
  })
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(radius * 12, radius * 12), material)
  mesh.rotation.x = -Math.PI / 2
  mesh.position.set(center.x, y, center.z)
  mesh.renderOrder = -1
  return mesh
}

const props = withDefaults(
  defineProps<{
    url: string
    fileName?: string
    autoRotate?: boolean
    minimal?: boolean
  }>(),
  { autoRotate: false, minimal: false }
)

const emit = defineEmits<{
  loaded: []
  pick: [result: PickResult]
  changed: []
  'measurement-added': [measurement: MeasurementInfo]
}>()

type IfcWorld = OBC.SimpleWorld<OBC.SimpleScene, OBC.OrthoPerspectiveCamera, OBC.SimpleRenderer>

const FEATURES: ViewerFeature[] = [
  'tree', 'groups', 'properties', 'visibility', 'colors', 'xray',
  'clipping', 'measure', 'notes', 'walk', 'projection', 'presets',
]

const HIGHLIGHT_MATERIAL: MaterialDefinition = {
  color: new THREE.Color(0x3b82f6),
  renderedFaces: RenderedFaces.TWO,
  opacity: 1,
  transparent: false,
}
const XRAY_OPACITY = 0.22
const EYE_HEIGHT = 1.6
const WALK_SPEED = 2.5 // m/s, Shift ×3
const CHANGE_DEBOUNCE_MS = 400
const CLICK_TOLERANCE_PX = 5

const containerRef = ref<HTMLElement | null>(null)
const isLoading = ref(true)
const loadProgress = ref(0)
const errorMessage = ref('')

const AUTO_ROTATE_SPEED = 0.18
const AUTO_ROTATE_RESUME_DELAY_MS = 1500
let autoRotateRafId: number | null = null
let autoRotateLastTime = 0
let autoRotatePaused = false
let autoRotateResumeTimer: ReturnType<typeof setTimeout> | null = null
let cameraControlStartHandler: (() => void) | null = null
let cameraControlEndHandler: (() => void) | null = null

let components: OBC.Components | null = null
let world: IfcWorld | null = null
let fragments: OBC.FragmentsManager | null = null
let loadedModel: FragmentsModel | null = null
let clipper: OBC.Clipper | null = null
let overlay: MeasureOverlay | null = null

const { theme } = useTheme()
const { t } = useI18n()
// Theme-dependent scene objects, updated live in applySceneTheme()
let grid: OBC.SimpleGrid | null = null
let blueprintFloor: THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial> | null = null
let shadowGround: THREE.Mesh<THREE.PlaneGeometry, THREE.ShadowMaterial> | null = null
let hemiLight: THREE.HemisphereLight | null = null

function applySceneTheme() {
  const palette = SCENE_PALETTE[theme.value]
  const scene = world?.scene?.three
  if (scene) {
    // Minimal (landing) mode is transparent so the CSS backdrop shows through
    scene.background = props.minimal ? null : new THREE.Color(palette.viewerBg)
  }
  if (grid) grid.config.color = new THREE.Color(palette.grid)
  if (blueprintFloor) {
    blueprintFloor.material.uniforms.uColor.value.setHex(palette.blueprint)
    blueprintFloor.material.uniforms.uOpacity.value = palette.blueprintOpacity
  }
  if (shadowGround) shadowGround.material.opacity = palette.shadowOpacity
  if (hemiLight) {
    hemiLight.color.setHex(palette.sky)
    hemiLight.groundColor.setHex(palette.ground)
  }
}

watch(theme, applySceneTheme)

// ---------------------------------------------------------------------------
// Reactive API state
// ---------------------------------------------------------------------------

const ready = ref(false)
const tool = ref<ViewerTool>('select')
const projection = ref<Projection>('perspective')
const walking = ref(false)
const selection = ref<SelectionInfo | null>(null)
const groups = ref<GroupInfo[]>([])
const hiddenGroups = ref<string[]>([])
const xray = ref(false)
const clipCount = ref(0)
const measurements = ref<MeasurementInfo[]>([])
// Bumped whenever element visibility / colours change (drives computed flags)
const visibilityVersion = ref(0)
const colorVersion = ref(0)

const visibilityModified = computed(() => {
  void visibilityVersion.value
  return hiddenIds.size > 0 || isolatedIds !== null || hiddenGroups.value.length > 0
})
const hasColors = computed(() => {
  void colorVersion.value
  return colorMap.size > 0
})
// Legacy flags used by ViewerView / SharedViewerView until they migrate
const hasSelection = computed(() => selection.value !== null)
const hasHiddenItems = computed(() => visibilityModified.value)

// ---------------------------------------------------------------------------
// Model indexes (built once after load)
// ---------------------------------------------------------------------------

let guidByLocal = new Map<number, string>()
let localByGuid = new Map<string, number>()
let categoryByLocal = new Map<number, string>() // pretty class name
let geometryIds = new Set<number>()
let groupItems = new Map<string, number[]>() // pretty class → items with geometry
let modelBox = new THREE.Box3()
let initialCamera: CameraState | null = null
let treeCache: TreeNode[] | null = null
let treeGroupItems = new Map<string, number[]>() // synthetic tree ids → item ids

// Scene state (local ids)
let selectedIds: number[] = []
let hiddenIds = new Set<number>()
let isolatedIds: Set<number> | null = null
let colorMap = new Map<string, Set<number>>() // '#rrggbb' → items

// Measurement in progress
let measureStart: THREE.Vector3 | null = null

// Pending applyState() before the model is loaded
let pendingState: ViewState | null = null
let pendingResolvers: (() => void)[] = []

// 'changed' suppression during programmatic updates (applyState)
let suppressChanged = 0
let changeTimer: ReturnType<typeof setTimeout> | null = null

function notifyChanged() {
  if (suppressChanged > 0) return
  emit('changed')
}

// Camera at the start of a user interaction; a click without movement is not a change
let cameraAtControlStart: string | null = null

function onControlStart() {
  if (changeTimer === null) cameraAtControlStart = JSON.stringify(getCamera())
}

function scheduleCameraChanged() {
  if (suppressChanged > 0) return
  if (changeTimer) clearTimeout(changeTimer)
  changeTimer = setTimeout(() => {
    changeTimer = null
    const before = cameraAtControlStart
    cameraAtControlStart = null
    if (before !== null && before === JSON.stringify(getCamera())) return
    notifyChanged()
  }, CHANGE_DEBOUNCE_MS)
}

/** Serialises async scene operations so rapid clicks can't interleave worker calls. */
let opChain: Promise<unknown> = Promise.resolve()
function serial<T>(fn: () => Promise<T>): Promise<T> {
  const run = opChain.then(fn, fn)
  opChain = run.then(
    () => undefined,
    () => undefined,
  )
  return run
}

function getCameraComponent(): OBC.OrthoPerspectiveCamera | null {
  return world?.camera ?? null
}

async function refreshFragments() {
  if (fragments?.initialized) await fragments.core.update(true)
}

// ---------------------------------------------------------------------------
// Id helpers
// ---------------------------------------------------------------------------

function toGuids(ids: Iterable<number>): string[] {
  const out: string[] = []
  for (const id of ids) {
    const g = guidByLocal.get(id)
    if (g) out.push(g)
  }
  return out
}

/** Resolves API ids (GUIDs or synthetic tree ids) to local ids. */
function resolveIds(ids: string[]): number[] {
  const out = new Set<number>()
  for (const id of ids) {
    const local = localByGuid.get(id)
    if (local !== undefined) {
      out.add(local)
      continue
    }
    const group = treeGroupItems.get(id)
    if (group) for (const l of group) out.add(l)
  }
  return [...out]
}

/** Local ids plus all their descendants (spatial / aggregation children). */
async function withDescendants(ids: number[]): Promise<number[]> {
  const model = loadedModel
  if (!model || ids.length === 0) return ids
  const all = new Set(ids)
  let frontier = ids
  for (let depth = 0; depth < 12 && frontier.length > 0; depth++) {
    let children: number[] = []
    try {
      children = await model.getItemsChildren(frontier)
    } catch {
      break
    }
    frontier = children.filter((c) => !all.has(c))
    for (const c of frontier) all.add(c)
  }
  return [...all]
}

/** Only items with geometry matter for visibility / colours / state. */
function geometryOnly(ids: Iterable<number>): number[] {
  const out: number[] = []
  for (const id of ids) if (geometryIds.has(id)) out.push(id)
  return out
}

// ---------------------------------------------------------------------------
// Appearance: x-ray, colours and selection highlight share the fragments
// "highlight" slot, so they are always re-applied together.
// ---------------------------------------------------------------------------

async function restyle(scope?: number[]) {
  const model = loadedModel
  if (!model) return
  if (scope && scope.length === 0) return
  const inScope = scope ? new Set(scope) : null
  await model.resetHighlight(scope)
  if (xray.value) await model.setOpacity(scope, XRAY_OPACITY)
  for (const [hex, set] of colorMap) {
    const ids = inScope ? [...set].filter((id) => inScope.has(id)) : [...set]
    if (ids.length) await model.setColor(ids, new THREE.Color(hex))
  }
  const sel = inScope ? selectedIds.filter((id) => inScope.has(id)) : selectedIds
  if (sel.length) {
    // Selection replaces colour/x-ray so it always reads clearly
    await model.resetHighlight(sel)
    await model.highlight(sel, HIGHLIGHT_MATERIAL)
  }
  await refreshFragments()
}

// ---------------------------------------------------------------------------
// Visibility
// ---------------------------------------------------------------------------

async function applyVisibility() {
  const model = loadedModel
  if (!model) return
  const hide = new Set<number>(hiddenIds)
  if (isolatedIds) {
    for (const id of geometryIds) if (!isolatedIds.has(id)) hide.add(id)
  }
  for (const g of hiddenGroups.value) {
    for (const id of groupItems.get(g) ?? []) hide.add(id)
  }
  await model.setVisible(undefined, true)
  if (hide.size) await model.setVisible([...hide], false)
  visibilityVersion.value++
  await refreshFragments()
}

function isVisibleLocal(id: number): boolean {
  if (hiddenIds.has(id)) return false
  if (isolatedIds && !isolatedIds.has(id)) return false
  const cat = categoryByLocal.get(id)
  if (cat && hiddenGroups.value.includes(cat)) return false
  return true
}

// ---------------------------------------------------------------------------
// Selection
// ---------------------------------------------------------------------------

let selectionToken = 0

async function setSelection(next: number[]) {
  const prev = selectedIds
  selectedIds = [...new Set(next)]
  await restyle([...new Set([...prev, ...selectedIds])])
  await updateSelectionInfo()
}

async function updateSelectionInfo() {
  const model = loadedModel
  const token = ++selectionToken
  const ids = selectedIds
  if (!model || ids.length === 0) {
    selection.value = null
    return
  }
  const guids = toGuids(ids)
  if (ids.length === 1) {
    const category = categoryByLocal.get(ids[0]) ?? 'Element'
    let info: SelectionInfo = { ids: guids, title: category, groups: [] }
    try {
      const [item] = await model.getItemsData(ids, {
        attributesDefault: true,
        relations: {
          IsDefinedBy: { attributes: true, relations: true },
          IsTypedBy: { attributes: true, relations: false },
          HasAssociations: { attributes: true, relations: false },
          ContainedInStructure: { attributes: true, relations: false },
          Decomposes: { attributes: true, relations: false },
        },
        relationsDefault: { attributes: false, relations: false },
      })
      if (item) {
        const name = itemName(item)
        info = { ids: guids, title: category, subtitle: name || undefined, groups: itemToGroups(item) }
      }
    } catch (err) {
      console.warn('Error loading properties:', err)
    }
    if (token === selectionToken) selection.value = info
    return
  }
  const counts = new Map<string, number>()
  for (const id of ids) {
    const c = categoryByLocal.get(id) ?? 'Element'
    counts.set(c, (counts.get(c) ?? 0) + 1)
  }
  const title = counts.size === 1 ? [...counts.keys()][0] : 'Multiple'
  const props: [string, string][] = [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([c, n]) => [c, String(n)])
  selection.value = { ids: guids, title, subtitle: `${ids.length}`, groups: [{ name: 'Selection', props }] }
}

// ---------------------------------------------------------------------------
// Picking
// ---------------------------------------------------------------------------

interface Hit {
  point: THREE.Vector3
  localId: number
  normal?: THREE.Vector3
  snap: SnapKind
  result: RaycastResult
}

function activePlanes(): THREE.Plane[] {
  return world?.renderer?.three.clippingPlanes ?? []
}

function isClippedAway(p: THREE.Vector3): boolean {
  return activePlanes().some((plane) => plane.distanceToPoint(p) < -1e-4)
}

function snapKindOf(r: RaycastResult): SnapKind {
  if (r.snappingClass === SnappingClass.POINT) return 'vertex'
  if (r.snappingClass === SnappingClass.LINE) return 'edge'
  return 'face'
}

async function pickAt(clientX: number, clientY: number, snapping = false): Promise<Hit | null> {
  const model = loadedModel
  const cam = getCameraComponent()
  const canvas = world?.renderer?.three.domElement
  if (!model || !cam || !canvas) return null
  // Fragments 3.x expects the mouse in client pixels (it converts to NDC itself)
  const data = { camera: cam.three, mouse: new THREE.Vector2(clientX, clientY), dom: canvas }
  const clipping = activePlanes().length > 0
  let results: RaycastResult[] = []
  try {
    if (snapping) {
      results =
        (await model.raycastWithSnapping({
          ...data,
          snappingClasses: [SnappingClass.POINT, SnappingClass.LINE, SnappingClass.FACE],
        })) ?? []
    }
    if (results.length === 0) {
      if (clipping) {
        results = (await model.raycastAll(data)) ?? []
      } else {
        const r = await model.raycast(data)
        results = r ? [r] : []
      }
    }
  } catch (err) {
    console.warn('Raycast error:', err)
    return null
  }
  const valid = results.filter((r) => !isClippedAway(r.point) && isVisibleLocal(r.localId))
  if (valid.length === 0) return null
  let best = valid[0]
  if (snapping) {
    // Nearest surface first, then prefer vertex > edge > face snaps on it
    const nearest = Math.min(...valid.map((r) => r.rayDistance ?? r.distance))
    const rank = (r: RaycastResult) =>
      r.snappingClass === SnappingClass.POINT ? 0 : r.snappingClass === SnappingClass.LINE ? 1 : 2
    const candidates = valid.filter((r) => (r.rayDistance ?? r.distance) <= nearest + 0.25)
    candidates.sort((a, b) => rank(a) - rank(b) || a.distance - b.distance)
    best = candidates[0] ?? best
  } else {
    valid.sort((a, b) => (a.rayDistance ?? a.distance) - (b.rayDistance ?? b.distance))
    best = valid[0]
  }
  return {
    point: best.point.clone(),
    localId: best.localId,
    normal: best.normal?.clone(),
    snap: snapping ? snapKindOf(best) : 'face',
    result: best,
  }
}

// ---------------------------------------------------------------------------
// Pointer handling (interactive mode only)
// ---------------------------------------------------------------------------

let pointerDown: { x: number; y: number; time: number; button: number; onGizmo: boolean } | null = null

function gizmoActive(): boolean {
  if (!clipper) return false
  for (const [, plane] of clipper.list) {
    if (plane.controls.axis !== null || plane.controls.dragging) return true
  }
  return false
}

function onPointerDown(e: PointerEvent) {
  pointerDown = { x: e.clientX, y: e.clientY, time: performance.now(), button: e.button, onGizmo: gizmoActive() }
}

function onPointerUp(e: PointerEvent) {
  const down = pointerDown
  pointerDown = null
  if (!down || down.button !== 0 || e.button !== 0 || down.onGizmo) return
  const moved = Math.hypot(e.clientX - down.x, e.clientY - down.y)
  if (moved > CLICK_TOLERANCE_PX || performance.now() - down.time > 700) return
  void serial(() => handleClick(e))
}

async function handleClick(e: PointerEvent) {
  if (!loadedModel || !ready.value) return
  const additive = e.ctrlKey || e.metaKey || e.shiftKey
  switch (tool.value) {
    case 'select': {
      const hit = await pickAt(e.clientX, e.clientY)
      if (!hit) {
        if (!additive) await setSelection([])
        return
      }
      if (additive) {
        const exists = selectedIds.includes(hit.localId)
        await setSelection(exists ? selectedIds.filter((id) => id !== hit.localId) : [...selectedIds, hit.localId])
      } else {
        await setSelection([hit.localId])
      }
      return
    }
    case 'note': {
      const hit = await pickAt(e.clientX, e.clientY)
      if (!hit) return
      emit('pick', { point: vec(hit.point), elementId: guidByLocal.get(hit.localId) })
      return
    }
    case 'measure': {
      const hit = await pickAt(e.clientX, e.clientY, true)
      if (!hit) return
      if (!measureStart) {
        measureStart = hit.point.clone()
        overlay?.showPreview(measureStart, null)
        return
      }
      const a = measureStart
      measureStart = null
      overlay?.showPreview(null, null)
      if (a.distanceTo(hit.point) < 1e-6) return
      const m = addMeasurement({ id: newId(), a: vec(a), b: vec(hit.point) })
      emit('measurement-added', m)
      notifyChanged()
      return
    }
    case 'clip': {
      const hit = await pickAt(e.clientX, e.clientY)
      if (!hit) return
      createPlaneFromHit(hit)
      notifyChanged()
      return
    }
  }
}

// Hover snapping while measuring (one raycast in flight at a time)
let hoverBusy = false
let hoverPending: { x: number; y: number } | null = null

function onPointerMove(e: PointerEvent) {
  if (tool.value !== 'measure' || !ready.value) return
  if (e.buttons !== 0) {
    overlay?.showSnap(null)
    return
  }
  hoverPending = { x: e.clientX, y: e.clientY }
  if (!hoverBusy) void processHover()
}

async function processHover() {
  hoverBusy = true
  try {
    while (hoverPending) {
      const { x, y } = hoverPending
      hoverPending = null
      const hit = await pickAt(x, y, true)
      if (tool.value !== 'measure') {
        overlay?.showSnap(null)
        break
      }
      overlay?.showSnap(hit?.point ?? null, hit?.snap)
      if (measureStart) overlay?.showPreview(measureStart, hit?.point ?? null)
    }
  } finally {
    hoverBusy = false
  }
}

function onPointerLeave() {
  hoverPending = null
  overlay?.showSnap(null)
}

function onWheel() {
  if (walking.value) return
  scheduleCameraChanged()
}

function onKeyDown(e: KeyboardEvent) {
  if (e.key === 'Escape' && measureStart) {
    cancelMeasure()
  }
  if (walking.value) onWalkKey(e, true)
}

function onKeyUp(e: KeyboardEvent) {
  if (walking.value) onWalkKey(e, false)
}

function cancelMeasure() {
  measureStart = null
  overlay?.showPreview(null, null)
}

// ---------------------------------------------------------------------------
// Measurements
// ---------------------------------------------------------------------------

function vec(v: THREE.Vector3): Vec3 {
  return [round(v.x), round(v.y), round(v.z)]
}

function round(n: number, digits = 4): number {
  const f = 10 ** digits
  return Math.round(n * f) / f
}

function addMeasurement(m: { id: string; a: Vec3; b: Vec3 }): MeasurementInfo {
  const length = new THREE.Vector3(...m.a).distanceTo(new THREE.Vector3(...m.b))
  const info: MeasurementInfo = { id: m.id, a: [...m.a], b: [...m.b], length }
  measurements.value = [...measurements.value.filter((x) => x.id !== m.id), info]
  overlay?.add(info)
  return info
}

function removeMeasurement(id: string) {
  if (!measurements.value.some((m) => m.id === id)) return
  measurements.value = measurements.value.filter((m) => m.id !== id)
  overlay?.remove(id)
  notifyChanged()
}

function clearMeasurementsInternal() {
  measurements.value = []
  overlay?.clear()
  cancelMeasure()
}

function clearMeasurements() {
  const had = measurements.value.length > 0
  clearMeasurementsInternal()
  if (had) notifyChanged()
}

// ---------------------------------------------------------------------------
// Clipping
// ---------------------------------------------------------------------------

function updateClipCount() {
  clipCount.value = clipper?.list.size ?? 0
}

function addPlane(normal: THREE.Vector3, point: THREE.Vector3) {
  if (!clipper || !world) return
  clipper.createFromNormalAndCoplanarPoint(world, normal.clone().normalize(), point.clone())
  updateClipCount()
  void refreshFragments()
}

function addClipPlane(axis: ClipAxis) {
  if (!world || modelBox.isEmpty()) return
  const center = modelBox.getCenter(new THREE.Vector3())
  const normal = new THREE.Vector3()
  if (axis === 'y') {
    // Horizontal section: keep the lower half (plan view)
    normal.set(0, -1, 0)
  } else {
    // Vertical section: keep the half away from the camera so the cut faces the viewer
    const camPos = world.camera.three.getWorldPosition(new THREE.Vector3())
    const i = axis === 'x' ? 0 : 2
    const side = center.getComponent(i) - camPos.getComponent(i)
    normal.setComponent(i, side >= 0 ? 1 : -1)
  }
  addPlane(normal, center)
  notifyChanged()
}

function faceNormalFromHit(hit: Hit): THREE.Vector3 | null {
  if (hit.normal && hit.normal.lengthSq() > 0) return hit.normal.clone().normalize()
  const pts = hit.result.facePoints
  const idx = hit.result.faceIndices
  if (pts && idx && idx.length >= 3) {
    const v = (k: number) => new THREE.Vector3(pts[idx[k] * 3], pts[idx[k] * 3 + 1], pts[idx[k] * 3 + 2])
    const n = new THREE.Vector3().subVectors(v(1), v(0)).cross(new THREE.Vector3().subVectors(v(2), v(0)))
    if (n.lengthSq() > 0) return n.normalize()
  }
  if (pts && pts.length >= 9) {
    const a = new THREE.Vector3(pts[0], pts[1], pts[2])
    const b = new THREE.Vector3(pts[3], pts[4], pts[5])
    const c = new THREE.Vector3(pts[6], pts[7], pts[8])
    const n = new THREE.Vector3().subVectors(b, a).cross(new THREE.Vector3().subVectors(c, a))
    if (n.lengthSq() > 0) return n.normalize()
  }
  return null
}

/** Plane coplanar with the clicked face; clips the side facing the camera (same as OBC Clipper). */
function createPlaneFromHit(hit: Hit) {
  const cam = getCameraComponent()
  if (!cam) return
  const viewDir = cam.three.getWorldDirection(new THREE.Vector3())
  let n = faceNormalFromHit(hit) ?? viewDir.clone().negate()
  if (n.dot(viewDir) > 0) n = n.negate() // face normal towards the camera
  // Offset 1 mm towards the camera so the clicked face itself stays visible
  const point = hit.point.clone().addScaledVector(n, 0.001)
  addPlane(n.clone().negate(), point)
}

function clearClippingInternal() {
  clipper?.deleteAll()
  updateClipCount()
  void refreshFragments()
}

function clearClipping() {
  const had = clipCount.value > 0
  clearClippingInternal()
  if (had) notifyChanged()
}

function getClipStates(): ClipPlaneState[] {
  if (!clipper) return []
  const out: ClipPlaneState[] = []
  for (const [, plane] of clipper.list) {
    const n = plane.three.normal.clone().normalize()
    // Point on the plane closest to the gizmo position (stays valid after drags)
    const p = plane.three.projectPoint(plane.helper.position.clone(), new THREE.Vector3())
    out.push({ normal: [round(n.x, 6), round(n.y, 6), round(n.z, 6)], point: vec(p) })
  }
  return out
}

// ---------------------------------------------------------------------------
// Camera
// ---------------------------------------------------------------------------

function modelSphere(box = modelBox): THREE.Sphere {
  const sphere = new THREE.Sphere()
  if (box.isEmpty()) return new THREE.Sphere(new THREE.Vector3(), 10)
  box.getBoundingSphere(sphere)
  return sphere
}

async function visibleBox(): Promise<THREE.Box3> {
  const model = loadedModel
  if (!model) return modelBox.clone()
  if (!visibilityModified.value) return modelBox.clone()
  const ids = [...geometryIds].filter(isVisibleLocal)
  if (ids.length === 0) return modelBox.clone()
  try {
    const box = await model.getMergedBox(ids)
    return box.isEmpty() ? modelBox.clone() : box
  } catch {
    return modelBox.clone()
  }
}

function fitView() {
  const cam = getCameraComponent()
  if (!cam || !world) return
  if (props.minimal) {
    const meshes: THREE.Mesh[] = []
    world.scene.three.traverse((child: THREE.Object3D) => {
      // Skip the oversized floor helpers or the fit would zoom far out
      if ((child as THREE.Mesh).isMesh && child !== blueprintFloor && child !== shadowGround) {
        meshes.push(child as THREE.Mesh)
      }
    })
    if (meshes.length > 0) void cam.fit(meshes, 0.5)
    return
  }
  if (walking.value) exitWalk(false)
  void (async () => {
    const box = await visibleBox()
    await cam.controls.fitToSphere(modelSphere(box), true)
    notifyChanged()
  })()
}

const PRESET_ANGLES: Record<CameraPreset, [number, number]> = {
  // [azimuth, polar] in camera-controls spherical coordinates (Y up, azimuth 0 = +Z)
  iso: [Math.PI / 4, Math.PI / 2 - Math.atan(1 / Math.SQRT2)],
  top: [0, 0],
  front: [0, Math.PI / 2],
  back: [Math.PI, Math.PI / 2],
  right: [Math.PI / 2, Math.PI / 2],
  left: [-Math.PI / 2, Math.PI / 2],
}

function setPreset(preset: CameraPreset) {
  const cam = getCameraComponent()
  if (!cam) return
  if (walking.value) exitWalk(false)
  void (async () => {
    const [azimuth, polar] = PRESET_ANGLES[preset]
    const controls = cam.controls
    controls.normalizeRotations()
    const box = await visibleBox()
    await Promise.all([controls.rotateTo(azimuth, polar, true), controls.fitToSphere(modelSphere(box), true)])
    notifyChanged()
  })()
}

/** Height of the orthographic frustum in world units. */
function orthoHeight(): number {
  const cam = getCameraComponent()
  if (!cam) return 0
  const o = cam.threeOrtho
  return (o.top - o.bottom) / o.zoom
}

/** OBC derives the ortho frustum from the perspective depth with this factor. */
function obcDepthFactor(): number {
  const cam = getCameraComponent()
  const fov = cam ? cam.threePersp.fov : 60
  return 2 * Math.atan((fov * Math.PI) / 180 / 2)
}

function getCamera(): CameraState {
  const cam = getCameraComponent()
  if (!cam) return { position: [0, 0, 0], target: [0, 0, 0], projection: projection.value }
  const controls = cam.controls
  const position = controls.getPosition(new THREE.Vector3(), true)
  const target = controls.getTarget(new THREE.Vector3(), true)
  if (projection.value === 'orthographic') {
    // Store an equivalent perspective eye so the framing survives projection changes
    const dir = new THREE.Vector3().subVectors(target, position).normalize()
    const depth = orthoHeight() / obcDepthFactor()
    position.copy(target).addScaledVector(dir, -depth)
  }
  return { position: vec(position), target: vec(target), projection: projection.value }
}

async function setProjectionInternal(p: Projection) {
  const cam = getCameraComponent()
  if (!cam) return
  if (projection.value === p) return
  await cam.projection.set(p === 'orthographic' ? 'Orthographic' : 'Perspective')
  syncProjection()
}

function syncProjection() {
  const cam = getCameraComponent()
  if (!cam) return
  projection.value = cam.projection.current === 'Orthographic' ? 'orthographic' : 'perspective'
}

async function setCamera(camera: CameraState, animate = false): Promise<void> {
  const cam = getCameraComponent()
  if (!cam) return
  if (walking.value) exitWalk(false)
  const desired: Projection = camera.projection ?? projection.value
  const [px, py, pz] = camera.position
  const [tx, ty, tz] = camera.target
  if (desired === 'perspective' && projection.value === 'perspective') {
    await cam.controls.setLookAt(px, py, pz, tx, ty, tz, animate)
    return
  }
  // Orthographic framing is derived from the perspective eye: go through perspective
  await setProjectionInternal('perspective')
  await cam.controls.setLookAt(px, py, pz, tx, ty, tz, false)
  cam.controls.update(0)
  cam.threePersp.updateMatrixWorld()
  if (desired === 'orthographic') await setProjectionInternal('orthographic')
}

function toggleProjection() {
  const cam = getCameraComponent()
  if (!cam) return
  if (props.minimal) {
    void cam.projection.toggle()
    return
  }
  if (walking.value) exitWalk(false)
  void (async () => {
    await setProjectionInternal(projection.value === 'perspective' ? 'orthographic' : 'perspective')
    notifyChanged()
  })()
}

// ---------------------------------------------------------------------------
// Walk (first person)
// ---------------------------------------------------------------------------

interface OrbitSettings {
  camera: CameraState
  minDistance: number
  maxDistance: number
  azimuthRotateSpeed: number
  polarRotateSpeed: number
  wheel: number
  left: number
  right: number
}

let orbitSettings: OrbitSettings | null = null
const walkKeys = new Set<string>()
let walkRaf: number | null = null
let walkLastTime = 0
let walkMoved = false

const WALK_KEYS = new Set([
  'KeyW', 'KeyA', 'KeyS', 'KeyD', 'KeyQ', 'KeyE',
  'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ShiftLeft', 'ShiftRight',
])

function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  if (!el || !el.tagName) return false
  return el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)
}

function onWalkKey(e: KeyboardEvent, down: boolean) {
  if (!WALK_KEYS.has(e.code)) return
  if (down && isTypingTarget(e.target)) return
  if (down) {
    walkKeys.add(e.code)
    if (e.code.startsWith('Arrow')) e.preventDefault()
    if (walkRaf === null) {
      walkLastTime = performance.now()
      walkRaf = requestAnimationFrame(walkTick)
    }
  } else {
    walkKeys.delete(e.code)
  }
}

function walkTick(now: number) {
  const cam = getCameraComponent()
  const dt = Math.min((now - walkLastTime) / 1000, 0.1)
  walkLastTime = now
  const hasMove = [...walkKeys].some((k) => !k.startsWith('Shift'))
  if (!cam || !walking.value || !hasMove) {
    walkRaf = null
    if (walkMoved) {
      walkMoved = false
      scheduleCameraChanged()
    }
    return
  }
  const controls = cam.controls
  const pos = controls.getPosition(new THREE.Vector3())
  const target = controls.getTarget(new THREE.Vector3())
  const forward = new THREE.Vector3().subVectors(target, pos)
  forward.y = 0
  if (forward.lengthSq() < 1e-10) forward.set(0, 0, -1)
  forward.normalize()
  const right = new THREE.Vector3().crossVectors(forward, new THREE.Vector3(0, 1, 0)).normalize()
  const move = new THREE.Vector3()
  if (walkKeys.has('KeyW') || walkKeys.has('ArrowUp')) move.add(forward)
  if (walkKeys.has('KeyS') || walkKeys.has('ArrowDown')) move.sub(forward)
  if (walkKeys.has('KeyD') || walkKeys.has('ArrowRight')) move.add(right)
  if (walkKeys.has('KeyA') || walkKeys.has('ArrowLeft')) move.sub(right)
  if (walkKeys.has('KeyE')) move.y += 1
  if (walkKeys.has('KeyQ')) move.y -= 1
  if (move.lengthSq() > 0) {
    const fast = walkKeys.has('ShiftLeft') || walkKeys.has('ShiftRight')
    move.normalize().multiplyScalar(WALK_SPEED * (fast ? 3 : 1) * dt)
    target.add(move)
    void controls.moveTo(target.x, target.y, target.z, false)
    walkMoved = true
  }
  walkRaf = requestAnimationFrame(walkTick)
}

function onWalkWheel(e: WheelEvent) {
  if (!walking.value) return
  const cam = getCameraComponent()
  if (!cam) return
  e.preventDefault()
  const controls = cam.controls
  const pos = controls.getPosition(new THREE.Vector3())
  const target = controls.getTarget(new THREE.Vector3())
  const forward = new THREE.Vector3().subVectors(target, pos).normalize()
  const step = -Math.sign(e.deltaY) * Math.max(0.25, modelSphere().radius * 0.01)
  target.addScaledVector(forward, step)
  void controls.moveTo(target.x, target.y, target.z, true)
  scheduleCameraChanged()
}

/** Lowest surface under (x, z): a vertical ray cast with a temporary camera above the model. */
async function findFloorY(x: number, z: number): Promise<number | null> {
  const model = loadedModel
  const canvas = world?.renderer?.three.domElement
  if (!model || !canvas || modelBox.isEmpty()) return null
  const rect = canvas.getBoundingClientRect()
  if (rect.width === 0 || rect.height === 0) return null
  const probe = new THREE.PerspectiveCamera(30, rect.width / rect.height, 0.1, modelBox.max.y - modelBox.min.y + 10)
  probe.position.set(x, modelBox.max.y + 1, z)
  probe.up.set(0, 0, -1)
  probe.lookAt(x, modelBox.min.y - 1, z)
  probe.updateMatrixWorld()
  probe.updateProjectionMatrix()
  try {
    const mouse = new THREE.Vector2(rect.left + rect.width / 2, rect.top + rect.height / 2)
    const hits = (await model.raycastAll({ camera: probe, mouse, dom: canvas })) ?? []
    // Upward-facing surfaces only (slab tops, not their undersides)
    const ys = hits
      .filter((h) => isVisibleLocal(h.localId) && !isClippedAway(h.point) && (h.normal ? h.normal.y > 0.5 : true))
      .map((h) => h.point.y)
    return ys.length ? Math.min(...ys) : null
  } catch {
    return null
  }
}

async function enterWalk() {
  const cam = getCameraComponent()
  if (!cam || walking.value) return
  if (projection.value === 'orthographic') await setProjectionInternal('perspective')
  const controls = cam.controls
  orbitSettings = {
    camera: getCamera(),
    minDistance: controls.minDistance,
    maxDistance: controls.maxDistance,
    azimuthRotateSpeed: controls.azimuthRotateSpeed,
    polarRotateSpeed: controls.polarRotateSpeed,
    wheel: controls.mouseButtons.wheel,
    left: controls.mouseButtons.left,
    right: controls.mouseButtons.right,
  }
  const pos = controls.getPosition(new THREE.Vector3())
  const target = controls.getTarget(new THREE.Vector3())
  const dir = new THREE.Vector3().subVectors(target, pos)
  dir.y = 0
  if (dir.lengthSq() < 1e-10) dir.set(0, 0, -1)
  dir.normalize()
  // Stand on the lowest level, at the orbit target (clamped into the model footprint)
  const box = modelBox.isEmpty() ? new THREE.Box3(new THREE.Vector3(-1, 0, -1), new THREE.Vector3(1, 3, 1)) : modelBox
  const x = THREE.MathUtils.clamp(target.x, box.min.x, box.max.x)
  const z = THREE.MathUtils.clamp(target.z, box.min.z, box.max.z)
  const floor = (await findFloorY(x, z)) ?? box.min.y
  const eye = new THREE.Vector3(x, Math.min(floor + EYE_HEIGHT, box.max.y), z)
  const LOOK = 0.05
  controls.minDistance = LOOK
  controls.maxDistance = LOOK
  controls.azimuthRotateSpeed = -0.35
  controls.polarRotateSpeed = -0.35
  controls.mouseButtons.wheel = CameraControls.ACTION.NONE
  controls.mouseButtons.left = CameraControls.ACTION.ROTATE
  controls.mouseButtons.right = CameraControls.ACTION.TRUCK
  const look = eye.clone().addScaledVector(dir, LOOK)
  walking.value = true
  await controls.setLookAt(eye.x, eye.y, eye.z, look.x, look.y, look.z, true)
}

/** Restores orbit navigation and the camera from before the walk. */
function exitWalk(animate: boolean) {
  const cam = getCameraComponent()
  if (!cam || !walking.value || !orbitSettings) return
  const s = orbitSettings
  const controls = cam.controls
  controls.minDistance = s.minDistance
  controls.maxDistance = s.maxDistance
  controls.azimuthRotateSpeed = s.azimuthRotateSpeed
  controls.polarRotateSpeed = s.polarRotateSpeed
  controls.mouseButtons.wheel = s.wheel as typeof controls.mouseButtons.wheel
  controls.mouseButtons.left = s.left as typeof controls.mouseButtons.left
  controls.mouseButtons.right = s.right as typeof controls.mouseButtons.right
  walking.value = false
  walkKeys.clear()
  orbitSettings = null
  const [px, py, pz] = s.camera.position
  const [tx, ty, tz] = s.camera.target
  void controls.setLookAt(px, py, pz, tx, ty, tz, animate)
}

function setWalk(on: boolean) {
  if (props.minimal || !ready.value) return
  if (on === walking.value) return
  if (on) {
    cancelMeasure()
    void enterWalk().then(() => notifyChanged())
  } else {
    exitWalk(true)
    notifyChanged()
  }
}

// ---------------------------------------------------------------------------
// Tools
// ---------------------------------------------------------------------------

function setTool(next: ViewerTool) {
  if (props.minimal) return
  if (tool.value === 'measure' && next !== 'measure') {
    cancelMeasure()
    overlay?.showSnap(null)
  }
  tool.value = next
  const canvas = world?.renderer?.three.domElement
  if (canvas) canvas.style.cursor = next === 'select' ? '' : 'crosshair'
}

// ---------------------------------------------------------------------------
// Public API: selection / visibility / colours / x-ray
// ---------------------------------------------------------------------------

function selectIds(ids: string[]): Promise<void> {
  return serial(async () => {
    if (!loadedModel) return
    await setSelection(resolveIds(ids))
  })
}

function clearSelection() {
  void serial(() => setSelection([]))
}

function focusSelection() {
  const cam = getCameraComponent()
  const model = loadedModel
  if (!cam || !model || selectedIds.length === 0) return
  if (walking.value) exitWalk(false)
  void (async () => {
    const box = await model.getMergedBox(selectedIds)
    if (box.isEmpty()) return
    const sphere = box.getBoundingSphere(new THREE.Sphere())
    sphere.radius = Math.max(sphere.radius * 1.15, 0.5)
    await cam.controls.fitToSphere(sphere, true)
    notifyChanged()
  })()
}

function hideSelected(): Promise<void> {
  return serial(async () => {
    if (!loadedModel || selectedIds.length === 0) return
    const ids = geometryOnly(await withDescendants(selectedIds))
    for (const id of ids) {
      hiddenIds.add(id)
      isolatedIds?.delete(id)
    }
    await setSelection([])
    await applyVisibility()
    notifyChanged()
  })
}

function isolateSelected(): Promise<void> {
  return serial(async () => {
    if (!loadedModel || selectedIds.length === 0) return
    const ids = geometryOnly(await withDescendants(selectedIds))
    isolatedIds = new Set(ids)
    for (const id of ids) hiddenIds.delete(id)
    await setSelection([])
    await applyVisibility()
    notifyChanged()
  })
}

function showAll(): Promise<void> {
  return serial(async () => {
    if (!loadedModel) return
    const had = visibilityModified.value
    hiddenIds = new Set()
    isolatedIds = null
    hiddenGroups.value = []
    await applyVisibility()
    if (had) notifyChanged()
  })
}

function setGroupVisible(groupId: string, visible: boolean): Promise<void> {
  return serial(async () => {
    if (!loadedModel || !groupItems.has(groupId)) return
    const set = new Set(hiddenGroups.value)
    if (visible === !set.has(groupId)) return
    if (visible) set.delete(groupId)
    else set.add(groupId)
    hiddenGroups.value = groups.value.map((g) => g.id).filter((id) => set.has(id))
    if (!visible) {
      const items = new Set(groupItems.get(groupId))
      const keep = selectedIds.filter((id) => !items.has(id))
      if (keep.length !== selectedIds.length) await setSelection(keep)
    }
    await applyVisibility()
    notifyChanged()
  })
}

function setNodesVisible(ids: string[], visible: boolean): Promise<void> {
  return serial(async () => {
    if (!loadedModel) return
    const locals = geometryOnly(await withDescendants(resolveIds(ids)))
    if (locals.length === 0) return
    for (const id of locals) {
      if (visible) {
        hiddenIds.delete(id)
        isolatedIds?.add(id)
      } else {
        hiddenIds.add(id)
        isolatedIds?.delete(id)
      }
    }
    if (!visible) {
      const gone = new Set(locals)
      const keep = selectedIds.filter((id) => !gone.has(id))
      if (keep.length !== selectedIds.length) await setSelection(keep)
    }
    await applyVisibility()
    notifyChanged()
  })
}

function normalizeHex(hex: string): string | null {
  const m = /^#?([0-9a-f]{6}|[0-9a-f]{3})$/i.exec(hex.trim())
  if (!m) return null
  let h = m[1].toLowerCase()
  if (h.length === 3) h = h.split('').map((c) => c + c).join('')
  return `#${h}`
}

function colorSelected(hex: string): Promise<void> {
  return serial(async () => {
    const color = normalizeHex(hex)
    if (!loadedModel || !color || selectedIds.length === 0) return
    const ids = geometryOnly(await withDescendants(selectedIds))
    for (const [key, set] of colorMap) {
      for (const id of ids) set.delete(id)
      if (set.size === 0) colorMap.delete(key)
    }
    const set = colorMap.get(color) ?? new Set<number>()
    for (const id of ids) set.add(id)
    if (set.size) colorMap.set(color, set)
    colorVersion.value++
    // Paint is visible right away: drop the selection highlight on painted items
    await setSelection([])
    await restyle(ids)
    notifyChanged()
  })
}

function clearColors(): Promise<void> {
  return serial(async () => {
    if (!loadedModel || colorMap.size === 0) return
    colorMap = new Map()
    colorVersion.value++
    await restyle()
    notifyChanged()
  })
}

function setXray(on: boolean): Promise<void> {
  return serial(async () => {
    if (!loadedModel || xray.value === on) return
    xray.value = on
    await restyle()
    notifyChanged()
  })
}

// ---------------------------------------------------------------------------
// Tree
// ---------------------------------------------------------------------------

const SPATIAL_CLASSES = new Set([
  'IFCPROJECT', 'IFCSITE', 'IFCBUILDING', 'IFCBUILDINGSTOREY', 'IFCFACILITY', 'IFCFACILITYPART',
  'IFCBRIDGE', 'IFCBRIDGEPART', 'IFCROAD', 'IFCROADPART', 'IFCRAILWAY', 'IFCRAILWAYPART',
  'IFCMARINEFACILITY', 'IFCMARINEPART',
])

async function loadTree(): Promise<TreeNode[]> {
  const model = loadedModel
  if (!model) return []
  if (treeCache) return treeCache
  const root: SpatialTreeItem = await model.getSpatialStructure()
  // Names for every node with a local id
  const ids: number[] = []
  const collect = (n: SpatialTreeItem) => {
    if (n.localId !== null && n.localId !== undefined) ids.push(n.localId)
    for (const c of n.children ?? []) collect(c)
  }
  collect(root)
  const names = new Map<number, string>()
  try {
    const data = await model.getItemsData(ids, {
      attributesDefault: false,
      attributes: ['Name'],
      relationsDefault: { attributes: false, relations: false },
    })
    data.forEach((item, i) => {
      const name = itemName(item)
      if (name) names.set(ids[i], name)
    })
  } catch (err) {
    console.warn('Could not load element names:', err)
  }

  treeGroupItems = new Map()
  const convert = (n: SpatialTreeItem, parentKey: string): TreeNode[] => {
    const children = n.children ?? []
    if (n.localId === null || n.localId === undefined) {
      // Fragments wraps every level in a class node; keep those only where they
      // group several elements (e.g. 'IfcBeam' under a storey)
      if (!n.category || SPATIAL_CLASSES.has(n.category.toUpperCase()) || children.length <= 1) {
        return children.flatMap((c) => convert(c, parentKey))
      }
      const id = `group:${parentKey}:${n.category}`
      const kids = children.flatMap((c) => convert(c, id))
      const items: number[] = []
      const gather = (x: SpatialTreeItem) => {
        if (x.localId !== null && x.localId !== undefined) items.push(x.localId)
        for (const c of x.children ?? []) gather(c)
      }
      gather(n)
      treeGroupItems.set(id, items)
      return [{ id, name: prettyIfcClass(n.category), category: prettyIfcClass(n.category), children: kids, hasGeometry: false }]
    }
    const localId = n.localId
    const guid = guidByLocal.get(localId) ?? `local:${localId}`
    if (!guidByLocal.has(localId)) treeGroupItems.set(guid, [localId])
    const category = categoryByLocal.get(localId) ?? (n.category ? prettyIfcClass(n.category) : undefined)
    return [{
      id: guid,
      name: names.get(localId) || category || guid,
      category,
      children: children.flatMap((c) => convert(c, guid)),
      hasGeometry: geometryIds.has(localId),
    }]
  }
  treeCache = convert(root, 'root')
  return treeCache
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

function getState(): EngineState {
  const colors: ColorOverride[] = []
  for (const [color, set] of colorMap) {
    const ids = toGuids(set)
    if (ids.length) colors.push({ ids, color })
  }
  return {
    camera: getCamera(),
    hidden: toGuids(hiddenIds),
    isolated: isolatedIds ? toGuids(isolatedIds) : null,
    hiddenGroups: [...hiddenGroups.value],
    colors,
    xray: xray.value,
    clipping: getClipStates(),
    measurements: measurements.value.map((m) => ({ id: m.id, a: [...m.a], b: [...m.b] })),
  }
}

async function applyStateNow(state: ViewState) {
  suppressChanged++
  try {
    if (walking.value) exitWalk(false)
    cancelMeasure()
    overlay?.showSnap(null)

    // Scene reset to defaults
    selectedIds = []
    selection.value = null
    hiddenIds = new Set(geometryOnly(resolveIds(state.hidden ?? [])))
    isolatedIds = state.isolated ? new Set(geometryOnly(resolveIds(state.isolated))) : null
    const wanted = new Set(state.hiddenGroups ?? [])
    hiddenGroups.value = groups.value.map((g) => g.id).filter((id) => wanted.has(id))
    colorMap = new Map()
    for (const c of state.colors ?? []) {
      const hex = normalizeHex(c.color)
      if (!hex) continue
      const set = colorMap.get(hex) ?? new Set<number>()
      for (const id of geometryOnly(resolveIds(c.ids))) {
        for (const other of colorMap.values()) other.delete(id)
        set.add(id)
      }
      if (set.size) colorMap.set(hex, set)
    }
    colorVersion.value++
    xray.value = !!state.xray
    await applyVisibility()
    await restyle()

    clearClippingInternal()
    for (const p of state.clipping ?? []) {
      const n = new THREE.Vector3(...p.normal)
      if (n.lengthSq() === 0) continue
      addPlane(n, new THREE.Vector3(...p.point))
    }

    clearMeasurementsInternal()
    for (const m of state.measurements ?? []) addMeasurement(m)

    const camera = state.camera ?? initialCamera
    if (camera) await setCamera(camera, false)
    await refreshFragments()
  } finally {
    suppressChanged--
  }
}

function applyState(state: ViewState): Promise<void> {
  if (!ready.value) {
    // Applied right after load, before 'loaded' is emitted
    pendingState = state
    return new Promise((resolve) => pendingResolvers.push(resolve))
  }
  return serial(() => applyStateNow(state))
}

// ---------------------------------------------------------------------------
// Overlay helpers / images
// ---------------------------------------------------------------------------

function project(points: Vec3[]): ScreenPoint[] {
  const cam = getCameraComponent()
  const el = containerRef.value
  if (!cam || !el) return points.map(() => ({ x: 0, y: 0, visible: false }))
  const camera = cam.three
  camera.updateMatrixWorld()
  const w = el.clientWidth
  const h = el.clientHeight
  const v = new THREE.Vector3()
  return points.map(([x, y, z]) => {
    v.set(x, y, z).project(camera)
    const sx = ((v.x + 1) / 2) * w
    const sy = ((1 - v.y) / 2) * h
    const visible = v.z > -1 && v.z < 1 && sx >= 0 && sx <= w && sy >= 0 && sy <= h
    return { x: sx, y: sy, visible }
  })
}

function renderNow(hideHelpers: boolean) {
  const renderer = world?.renderer?.three
  const scene = world?.scene?.three
  const camera = world?.camera?.three
  if (!renderer || !scene || !camera) return null
  const restore: (() => void)[] = []
  if (hideHelpers) {
    if (clipper && clipper.list.size > 0 && clipper.visible) {
      clipper.visible = false
      restore.push(() => {
        if (clipper) clipper.visible = true
      })
    }
    overlay?.showSnap(null)
  }
  renderer.render(scene, camera)
  for (const r of restore) r()
  return renderer
}

async function screenshot(): Promise<Blob | null> {
  if (fragments?.initialized) await fragments.core.update(true)
  const renderer = renderNow(true)
  if (!renderer) return null
  return new Promise((resolve) => renderer.domElement.toBlob((b) => resolve(b), 'image/png'))
}

async function captureSnapshot(): Promise<Blob | null> {
  const renderer = world?.renderer?.three
  const scene = world?.scene?.three
  const camera = world?.camera?.three
  if (!renderer || !scene || !camera) return null
  if (fragments?.initialized) await fragments.core.update(true)
  renderer.render(scene, camera)
  return canvasToOgBlob(renderer.domElement)
}

// ---------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------

async function buildIndexes(model: FragmentsModel) {
  const localIds = await model.getLocalIds()
  const guids = await model.getGuidsByLocalIds(localIds)
  guidByLocal = new Map()
  localByGuid = new Map()
  localIds.forEach((id, i) => {
    const g = guids[i]
    if (g) {
      guidByLocal.set(id, g)
      localByGuid.set(g, id)
    }
  })
  geometryIds = new Set(await model.getItemsIdsWithGeometry())
  const byCategory = await model.getItemsOfCategories([/.*/])
  categoryByLocal = new Map()
  groupItems = new Map()
  for (const [raw, ids] of Object.entries(byCategory)) {
    const name = prettyIfcClass(raw)
    const withGeom: number[] = []
    for (const id of ids) {
      categoryByLocal.set(id, name)
      if (geometryIds.has(id)) withGeom.push(id)
    }
    if (withGeom.length) groupItems.set(name, [...(groupItems.get(name) ?? []), ...withGeom])
  }
  groups.value = [...groupItems.entries()]
    .map(([name, ids]) => ({ id: name, name, count: ids.length }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
}

function setupInteractive(model: FragmentsModel) {
  if (!world || !components) return
  const w = world
  const cam = w.camera
  const renderer = w.renderer
  if (!renderer) return
  const canvas = renderer.three.domElement

  // Clipping: global renderer planes clip fragments; also feed them to tile culling
  clipper = components.get(OBC.Clipper)
  clipper.setup({ color: new THREE.Color(0x5b8cff), opacity: 0.12, size: 5 })
  clipper.onAfterDrag.add(() => notifyChanged())
  clipper.onAfterDelete.add(updateClipCount)
  model.getClippingPlanesEvent = () => renderer.three.clippingPlanes

  overlay = new MeasureOverlay()
  w.scene.three.add(overlay.group)
  renderer.onBeforeUpdate.add(() => {
    const size = renderer.getSize()
    overlay?.setViewport(size.x, size.y, renderer.three.getPixelRatio())
  })

  // Camera: keep the ortho framing consistent across toggles; follow the active camera
  cam.projection.matchOrthoDistanceEnabled = true
  cam.projection.onChanged.add((camera: THREE.PerspectiveCamera | THREE.OrthographicCamera) => {
    model.useCamera(camera)
    syncProjection()
  })
  const radius = modelSphere().radius
  cam.controls.maxDistance = Math.max(300, radius * 20)
  cam.controls.addEventListener('controlstart', onControlStart)
  cam.controls.addEventListener('controlend', scheduleCameraChanged)

  canvas.addEventListener('pointerdown', onPointerDown)
  canvas.addEventListener('pointerup', onPointerUp)
  canvas.addEventListener('pointermove', onPointerMove)
  canvas.addEventListener('pointerleave', onPointerLeave)
  canvas.addEventListener('wheel', onWheel, { passive: true })
  canvas.addEventListener('wheel', onWalkWheel, { passive: false })
  window.addEventListener('keydown', onKeyDown)
  window.addEventListener('keyup', onKeyUp)
}

function teardownInteractive() {
  const canvas = world?.renderer?.three.domElement
  if (canvas) {
    canvas.removeEventListener('pointerdown', onPointerDown)
    canvas.removeEventListener('pointerup', onPointerUp)
    canvas.removeEventListener('pointermove', onPointerMove)
    canvas.removeEventListener('pointerleave', onPointerLeave)
    canvas.removeEventListener('wheel', onWheel)
    canvas.removeEventListener('wheel', onWalkWheel)
  }
  world?.camera?.controls?.removeEventListener('controlstart', onControlStart)
  world?.camera?.controls?.removeEventListener('controlend', scheduleCameraChanged)
  window.removeEventListener('keydown', onKeyDown)
  window.removeEventListener('keyup', onKeyUp)
  if (walkRaf !== null) cancelAnimationFrame(walkRaf)
  walkRaf = null
  if (changeTimer) clearTimeout(changeTimer)
  changeTimer = null
  overlay?.dispose()
  overlay = null
  clipper = null
}

function resetEngineState() {
  ready.value = false
  tool.value = 'select'
  projection.value = 'perspective'
  walking.value = false
  selection.value = null
  groups.value = []
  hiddenGroups.value = []
  xray.value = false
  clipCount.value = 0
  measurements.value = []
  guidByLocal = new Map()
  localByGuid = new Map()
  categoryByLocal = new Map()
  geometryIds = new Set()
  groupItems = new Map()
  modelBox = new THREE.Box3()
  initialCamera = null
  treeCache = null
  treeGroupItems = new Map()
  selectedIds = []
  hiddenIds = new Set()
  isolatedIds = null
  colorMap = new Map()
  measureStart = null
  orbitSettings = null
  walkKeys.clear()
  visibilityVersion.value++
  colorVersion.value++
}

async function initViewer() {
  if (!containerRef.value) return

  try {
    isLoading.value = true
    loadProgress.value = 5
    errorMessage.value = ''

    // Initialize components
    components = new OBC.Components()

    // Create world
    const worlds = components.get(OBC.Worlds)
    const w = worlds.create<OBC.SimpleScene, OBC.OrthoPerspectiveCamera, OBC.SimpleRenderer>()
    world = w

    // Setup renderer
    w.renderer = new OBC.SimpleRenderer(components, containerRef.value)

    // Setup scene
    w.scene = new OBC.SimpleScene(components)
    w.scene.setup()

    // Enable real-time shadows for the cinematic landing render
    if (props.minimal) {
      const renderer = w.renderer.three
      renderer.shadowMap.enabled = true
      renderer.shadowMap.type = THREE.PCFSoftShadowMap
    }

    // Setup camera
    w.camera = new OBC.OrthoPerspectiveCamera(components)

    // Initialize components
    components.init()

    // Create grid (skipped in minimal mode for a cleaner hero presentation)
    if (!props.minimal) {
      const grids = components.get(OBC.Grids)
      grid = grids.create(w)
    }
    applySceneTheme()

    loadProgress.value = 15

    // Setup fragments manager
    fragments = components.get(OBC.FragmentsManager)

    // Initialize fragments worker
    await fragments.init(fragmentsWorkerUrl)

    loadProgress.value = 25

    // Setup IFC loader
    const ifcLoader = components.get(OBC.IfcLoader)
    await ifcLoader.setup({
      autoSetWasm: false,
      wasm: {
        path: 'https://unpkg.com/web-ifc@0.0.76/',
        absolute: true,
      },
    })

    loadProgress.value = 40

    // Fetch the IFC file
    const response = await fetch(props.url)
    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.statusText}`)
    }

    loadProgress.value = 60

    const data = await response.arrayBuffer()
    const buffer = new Uint8Array(data)

    loadProgress.value = 75

    // Load the IFC model
    const model = await ifcLoader.load(buffer, true, props.fileName || 'model.ifc')
    loadedModel = model

    // Add model to the scene
    w.scene.three.add(model.object)

    // Set camera on model for tile-based LOD rendering
    const camera = w.camera
    model.useCamera(camera.three)

    // Hook fragments update into the render loop
    w.renderer.onBeforeUpdate.add(() => {
      if (fragments?.initialized) {
        fragments.core.update()
      }
    })

    loadProgress.value = 95

    // Wait a tick for tiles to start generating
    await new Promise(resolve => setTimeout(resolve, 100))
    await fragments.core.update(true)

    // Fit camera to the model bounding box
    const box = model.box
    modelBox = box.clone()
    if (!box.isEmpty()) {
      const sphere = new THREE.Sphere()
      box.getBoundingSphere(sphere)
      const helperGeom = new THREE.SphereGeometry(sphere.radius, 8, 8)
      const helperMesh = new THREE.Mesh(helperGeom)
      helperMesh.position.copy(sphere.center)
      // fit() uses radius = max box size * offset; 0.5 is the exact bounding sphere.
      // The landing frames slightly tighter (corners may crop briefly while rotating)
      await camera.fit([helperMesh], props.minimal ? 0.44 : 0.5)
      helperGeom.dispose()
    }

    // Cinematic sunlight + shadows for the minimal (landing) render
    if (props.minimal && !box.isEmpty()) {
      setupSunlightAndShadows(model, box)
    }

    await nextTick()

    if (props.minimal) {
      // Disable wheel zoom and pinch zoom so the landing page can still scroll over the viewer.
      const controls = camera.controls
      if (controls) {
        controls.mouseButtons.wheel = 0
        controls.touches.two = 0
      }
    } else {
      await buildIndexes(model)
      setupInteractive(model)
      initialCamera = getCamera()
    }

    if (props.autoRotate) {
      startAutoRotate()
    }

    ready.value = true
    if (pendingState) {
      const state = pendingState
      const resolvers = pendingResolvers
      pendingState = null
      pendingResolvers = []
      await serial(() => applyStateNow(state))
      for (const r of resolvers) r()
    }

    loadProgress.value = 100
    isLoading.value = false
    emit('loaded')
  } catch (err: unknown) {
    console.error('Error initializing viewer:', err)
    errorMessage.value = (err instanceof Error && err.message) || 'Failed to load the 3D model'
    isLoading.value = false
  }
}

function handleResize() {
  if (world?.renderer) {
    world.renderer.resize(undefined)
  }
}

function setupSunlightAndShadows(model: FragmentsModel, box: THREE.Box3) {
  const scene = world?.scene?.three
  if (!scene) return

  const size = new THREE.Vector3()
  const center = new THREE.Vector3()
  box.getSize(size)
  box.getCenter(center)
  const radius = Math.max(size.x, size.y, size.z, 1)

  const sun = new THREE.DirectionalLight(0xfff1cc, 2.2)
  sun.position.set(center.x + radius * 1.4, box.max.y + radius * 1.6, center.z + radius * 1.0)
  sun.target.position.copy(center)
  sun.castShadow = true
  sun.shadow.mapSize.set(2048, 2048)
  sun.shadow.camera.near = 0.1
  sun.shadow.camera.far = radius * 8
  const span = radius * 2.2
  sun.shadow.camera.left = -span
  sun.shadow.camera.right = span
  sun.shadow.camera.top = span
  sun.shadow.camera.bottom = -span
  sun.shadow.bias = -0.0005
  sun.shadow.normalBias = 0.02
  scene.add(sun)
  scene.add(sun.target)

  // Soft sky/ground fill so shaded faces keep some color instead of going flat grey
  hemiLight = new THREE.HemisphereLight(0xffffff, 0x000000, 0.6)
  scene.add(hemiLight)

  shadowGround = new THREE.Mesh(
    new THREE.PlaneGeometry(radius * 12, radius * 12),
    new THREE.ShadowMaterial({ opacity: 0.45 })
  )
  shadowGround.rotation.x = -Math.PI / 2
  shadowGround.position.set(center.x, box.min.y - 0.001, center.z)
  shadowGround.receiveShadow = true
  scene.add(shadowGround)

  blueprintFloor = createBlueprintFloor(center, radius, box.min.y - radius * 0.002)
  scene.add(blueprintFloor)

  applySceneTheme()

  const enableShadowsOnTree = (root: THREE.Object3D) => {
    root.traverse((child) => {
      const mesh = child as THREE.Mesh
      if (mesh.isMesh) {
        mesh.castShadow = true
        mesh.receiveShadow = true
      }
    })
  }
  enableShadowsOnTree(model.object)
}

function startAutoRotate() {
  const controls = world?.camera?.controls
  if (!controls) return

  stopAutoRotate()

  autoRotatePaused = false
  cameraControlStartHandler = () => {
    autoRotatePaused = true
    if (autoRotateResumeTimer) {
      clearTimeout(autoRotateResumeTimer)
      autoRotateResumeTimer = null
    }
  }
  cameraControlEndHandler = () => {
    if (autoRotateResumeTimer) clearTimeout(autoRotateResumeTimer)
    autoRotateResumeTimer = setTimeout(() => {
      autoRotatePaused = false
      autoRotateLastTime = performance.now()
    }, AUTO_ROTATE_RESUME_DELAY_MS)
  }
  controls.addEventListener('controlstart', cameraControlStartHandler)
  controls.addEventListener('controlend', cameraControlEndHandler)

  autoRotateLastTime = performance.now()
  const tick = (now: number) => {
    const dt = (now - autoRotateLastTime) / 1000
    autoRotateLastTime = now
    const c = world?.camera?.controls
    if (c && !autoRotatePaused) {
      c.azimuthAngle += AUTO_ROTATE_SPEED * dt
    }
    autoRotateRafId = requestAnimationFrame(tick)
  }
  autoRotateRafId = requestAnimationFrame(tick)
}

function stopAutoRotate() {
  if (autoRotateRafId !== null) {
    cancelAnimationFrame(autoRotateRafId)
    autoRotateRafId = null
  }
  if (autoRotateResumeTimer) {
    clearTimeout(autoRotateResumeTimer)
    autoRotateResumeTimer = null
  }
  const controls = world?.camera?.controls
  if (controls) {
    if (cameraControlStartHandler) controls.removeEventListener('controlstart', cameraControlStartHandler)
    if (cameraControlEndHandler) controls.removeEventListener('controlend', cameraControlEndHandler)
  }
  cameraControlStartHandler = null
  cameraControlEndHandler = null
  autoRotatePaused = false
}

function disposeViewer() {
  teardownInteractive()
  blueprintFloor?.geometry.dispose()
  blueprintFloor?.material.dispose()
  shadowGround?.geometry.dispose()
  shadowGround?.material.dispose()
  blueprintFloor = null
  shadowGround = null
  hemiLight = null
  grid = null
  if (components) {
    components.dispose()
    components = null
    world = null
    fragments = null
    loadedModel = null
  }
}

onMounted(() => {
  initViewer()
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  stopAutoRotate()
  disposeViewer()
  for (const r of pendingResolvers) r()
  pendingResolvers = []
})

watch(
  () => props.url,
  () => {
    stopAutoRotate()
    disposeViewer()
    resetEngineState()
    initViewer()
  }
)

const exposed = {
  kind: 'ifc' as const,
  features: FEATURES,
  ready,
  // Camera
  fitView,
  setPreset,
  toggleProjection,
  projection,
  getCamera,
  setCamera,
  setWalk,
  walking,
  // Interaction
  setTool,
  tool,
  // Selection
  selection,
  selectIds,
  clearSelection,
  focusSelection,
  // Visibility
  hideSelected,
  isolateSelected,
  showAll,
  visibilityModified,
  groups,
  hiddenGroups,
  setGroupVisible,
  loadTree,
  setNodesVisible,
  // Appearance
  colorSelected,
  clearColors,
  hasColors,
  xray,
  setXray,
  // Clipping
  addClipPlane,
  clearClipping,
  clipCount,
  // Measurements
  measurements,
  removeMeasurement,
  clearMeasurements,
  // Units / up axis: fixed for IFC (metres, Y-up after fragments' conversion)
  unitScale: 1,
  upAxis: 'y' as const,
  setFrame: async () => {},
  // Overlay / state / images
  project,
  getState,
  applyState,
  screenshot,
  captureSnapshot,
  // Legacy (old ViewerView / SharedViewerView)
  hasSelection,
  hasHiddenItems,
}

// Compile-time check: the unwrapped exposed object satisfies the shared contract
const conformsToApi: ShallowUnwrapRef<typeof exposed> extends ViewerApi ? true : never = true
void conformsToApi

defineExpose(exposed)
</script>

<template>
  <div :class="['ifc-viewer-wrapper', { minimal }]">
    <!-- Loading overlay -->
    <div v-if="isLoading" class="viewer-loading">
      <div class="loading-content">
        <div class="loading-spinner"></div>
        <p class="loading-text">{{ t('viewer.loadingModel') }}</p>
        <div class="loading-progress-bar">
          <div class="loading-progress-fill" :style="{ width: loadProgress + '%' }"></div>
        </div>
        <p class="loading-percent">{{ loadProgress }}%</p>
      </div>
    </div>

    <!-- Error overlay -->
    <div v-if="errorMessage && !isLoading" class="viewer-error">
      <div class="error-content">
        <div class="error-icon"><span class="mdi mdi-alert"></span></div>
        <h3>{{ t('viewer.loadError') }}</h3>
        <p>{{ errorMessage }}</p>
      </div>
    </div>

    <!-- 3D container -->
    <div ref="containerRef" class="ifc-container"></div>
  </div>
</template>

<style scoped>
.ifc-viewer-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 400px;
  background: var(--viewer-bg);
  border-radius: 12px;
  overflow: hidden;
}

.ifc-viewer-wrapper.minimal {
  background: transparent;
  border-radius: 0;
}

.ifc-container {
  width: 100%;
  height: 100%;
  min-height: 400px;
}

.ifc-container :deep(canvas) {
  width: 100% !important;
  height: 100% !important;
  outline: none;
}

/* Loading */
.viewer-loading,
.viewer-error {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--viewer-bg) 92%, transparent);
  backdrop-filter: blur(6px);
  z-index: 10;
}

.minimal .viewer-loading {
  background: transparent;
  backdrop-filter: none;
}

.loading-content {
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 2px solid var(--border-strong);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  color: var(--text);
  font-size: 0.9rem;
  font-weight: 500;
  margin: 0 0 1rem;
}

.loading-progress-bar {
  width: 180px;
  height: 3px;
  background: var(--surface-3);
  border-radius: 2px;
  overflow: hidden;
  margin: 0 auto 0.5rem;
}

.loading-progress-fill {
  height: 100%;
  background: var(--accent-gradient);
  border-radius: 2px;
  transition: width 0.3s var(--ease);
}

.loading-percent {
  color: var(--text-subtle);
  font-family: var(--font-mono);
  font-size: 0.75rem;
  margin: 0;
}

/* Error */
.error-content {
  text-align: center;
  padding: 2rem;
}

.error-icon {
  font-size: 2.5rem;
  color: var(--warning);
  margin-bottom: 0.75rem;
}

.error-content h3 {
  color: var(--text);
  margin: 0 0 0.5rem;
  font-size: 1.1rem;
}

.error-content p {
  color: var(--text-subtle);
  margin: 0;
  font-size: 0.85rem;
}
</style>
